/* Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5 · macrostructure: Index-First · F3 knobs: rows=tonal, columns=5, numbers=tabular
 * tone: operational-utilitarian · anchor hue: heritage-green · theme: Operational Architect · nav: N3 side-rail · enrichment: none
 * contrast: pass (40–41) · slop: pass (42–45) · responsive: pass (34, 49–57) · icons: pass (30)
 */
import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  PauseCircle,
  RefreshCw,
  Search,
  Trash2,
  UsersRound,
  X,
} from 'lucide-react';
import { StatusChip, ProgressBar, Button } from '@lms/ui';
import { useEnrollmentPage, useRemoveEnrollmentAccess } from '../hooks/useEnrollments';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type { EnrollmentStatus } from '@lms/shared/types';
import { useNotificationStore } from '../store/notifications';

type EnrollmentFilter = 'ALL' | EnrollmentStatus;

const statusOrder: EnrollmentFilter[] = ['ALL', 'ACTIVE', 'PAUSED', 'COMPLETED', 'DROPPED'];
const MAX_REMOVAL_BATCH = 50;

const statusCopy = {
  ACTIVE: copy.participants.activeStatus,
  PAUSED: copy.participants.pausedStatus,
  COMPLETED: copy.participants.completedStatus,
  DROPPED: copy.participants.droppedStatus,
} satisfies Record<EnrollmentStatus, typeof copy.participants.activeStatus>;

export function Participants() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<EnrollmentFilter>('ALL');
  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState<string[]>([]);
  const [removalEnrollmentIds, setRemovalEnrollmentIds] = useState<string[]>([]);
  const [removalReason, setRemovalReason] = useState('');
  const [isRemovalConfirmed, setIsRemovalConfirmed] = useState(false);
  const deferredSearch = useDeferredValue(search);
  const { language, t } = useLanguage();
  const { addNotification } = useNotificationStore();
  const { mutate: removeEnrollmentAccess, isPending: isRemovingAccess } = useRemoveEnrollmentAccess();
  const { data, isLoading, isFetching, error, refetch } = useEnrollmentPage({
    page,
    limit: 50,
    search: deferredSearch.trim() || undefined,
    status: status === 'ALL' ? undefined : status,
  });
  const filteredEnrollments = data?.items ?? [];
  const statusCounts = data?.summary.statusCounts;
  const currentRange = (() => {
    if (!data?.meta.total || filteredEnrollments.length === 0) return null;
    const from = (data.meta.page - 1) * data.meta.limit + 1;
    return { from, to: from + filteredEnrollments.length - 1, total: data.meta.total };
  })();
  const attentionCount = filteredEnrollments.filter(
    enrollment => enrollment.status === 'ACTIVE' && enrollment.progress < 50
  ).length;
  const totalMatching = data?.meta.total ?? 0;
  const activeMatching = statusCounts?.ACTIVE ?? 0;
  const selectedEnrollmentIdSet = new Set(selectedEnrollmentIds);
  const allVisibleSelected =
    filteredEnrollments.length > 0 &&
    filteredEnrollments.every(enrollment => selectedEnrollmentIdSet.has(enrollment.id));

  const toggleEnrollmentSelection = (enrollmentId: string) => {
    if (selectedEnrollmentIdSet.has(enrollmentId)) {
      setSelectedEnrollmentIds(current => current.filter(id => id !== enrollmentId));
      return;
    }
    if (selectedEnrollmentIds.length >= MAX_REMOVAL_BATCH) {
      addNotification({
        type: 'warning',
        title: t(copy.participants.removeAccess),
        message: t(copy.participants.removeAccessLimit),
      });
      return;
    }
    setSelectedEnrollmentIds(current => [...current, enrollmentId]);
  };

  const toggleVisibleEnrollmentSelection = () => {
    const visibleIds = filteredEnrollments.map(enrollment => enrollment.id);
    if (allVisibleSelected) {
      setSelectedEnrollmentIds(current => current.filter(id => !visibleIds.includes(id)));
      return;
    }
    const candidateIds = visibleIds.filter(id => !selectedEnrollmentIdSet.has(id));
    const newIds = candidateIds.slice(0, MAX_REMOVAL_BATCH - selectedEnrollmentIds.length);
    if (newIds.length < candidateIds.length) {
      addNotification({
        type: 'warning',
        title: t(copy.participants.removeAccess),
        message: t(copy.participants.removeAccessLimit),
      });
    }
    setSelectedEnrollmentIds(current => [...current, ...newIds]);
  };

  const openRemovalConfirmation = (enrollmentIds: string[]) => {
    setRemovalEnrollmentIds(enrollmentIds);
    setRemovalReason('');
    setIsRemovalConfirmed(false);
  };

  const closeRemovalConfirmation = () => {
    if (isRemovingAccess) return;
    setRemovalEnrollmentIds([]);
    setRemovalReason('');
    setIsRemovalConfirmed(false);
  };

  const submitRemoval = () => {
    if (
      removalEnrollmentIds.length === 0 ||
      removalReason.trim().length < 5 ||
      !isRemovalConfirmed
    ) {
      return;
    }

    removeEnrollmentAccess(
      {
        enrollmentIds: removalEnrollmentIds,
        reason: removalReason.trim(),
        confirmation: 'HAPUS',
      },
      {
        onSuccess: result => {
          const removedIds = new Set(result.removedEnrollmentIds);
          setSelectedEnrollmentIds(current => current.filter(id => !removedIds.has(id)));
          closeRemovalConfirmation();
          addNotification({
            type: 'success',
            title: t(copy.participants.removeAccess),
            message: t(copy.participants.removeAccessSuccess),
          });
        },
        onError: () => {
          addNotification({
            type: 'error',
            title: t(copy.participants.removeAccess),
            message: t(copy.participants.removeAccessFailure),
          });
        },
      }
    );
  };

  const selectStatus = (nextStatus: EnrollmentFilter) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setStatus('ALL');
    setPage(1);
  };

  const getStatusLabel = (enrollmentStatus: EnrollmentStatus) => t(statusCopy[enrollmentStatus]);

  return (
    <div className="space-y-8 pb-12 animate-fadeIn">
      <header className="grid gap-6 rounded-3xl bg-surface-container-low p-6 sm:p-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)] xl:items-end">
        <div className="min-w-0">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.15em] text-primary">
            <Bilingual {...copy.participants.directoryLabel} />
          </p>
          <h1 className="mt-3 max-w-3xl break-words wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...copy.participants.listTitle} />
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-on-surface-variant sm:text-base">
            <Bilingual {...copy.participants.listSubtitle} />
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-surface-container-lowest px-3 py-2 text-xs font-label font-bold text-on-surface">
            <UsersRound size={15} className="shrink-0 text-primary" />
            <span>{totalMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}</span>
            <span className="text-on-surface-variant">
              <Bilingual {...copy.participants.totalEnrollments} />
            </span>
          </div>
        </div>

        <section
          className="rounded-2xl bg-primary p-5 text-on-primary shadow-level1"
          aria-label={t(copy.participants.directoryLabel)}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.15em] text-on-primary/70">
                <Bilingual {...copy.participants.averageProgress} />
              </p>
              <p className="mt-3 font-headline text-4xl font-extrabold tabular-nums">
                {data?.summary.averageProgress ?? 0}%
              </p>
              <p className="mt-2 text-xs leading-relaxed text-on-primary/80">
                <Bilingual {...copy.participants.activeEnrollments} />:{' '}
                {activeMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-container-lowest text-primary transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-on-primary disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
              aria-label={t(isFetching ? copy.participants.refreshing : copy.participants.refresh)}
              title={t(isFetching ? copy.participants.refreshing : copy.participants.refresh)}
            >
              <RefreshCw size={17} className={isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </section>
      </header>

      <section
        className="rounded-3xl bg-surface-container-low p-5 sm:p-6"
        aria-labelledby="participant-filter-title"
      >
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="w-full max-w-xl">
            <label
              id="participant-filter-title"
              htmlFor="participant-search"
              className="block text-xs font-label font-bold text-on-surface"
            >
              <Bilingual {...copy.participants.searchLabel} />
            </label>
            <div className="relative mt-2">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                size={18}
              />
              <input
                id="participant-search"
                type="search"
                placeholder={
                  language === 'ar'
                    ? copy.participants.searchPlaceholder.ar
                    : copy.participants.searchPlaceholder.id
                }
                className="h-12 w-full rounded-xl bg-surface-container-lowest py-3 pl-12 pr-4 text-sm text-on-surface outline-none transition-colors placeholder:text-on-surface-variant/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <div className="min-w-0 xl:max-w-3xl">
            <p className="m-0 text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.participants.filterLabel} />
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {statusOrder.map(filterStatus => {
                const isSelected = status === filterStatus;
                const count =
                  filterStatus === 'ALL' ? totalMatching : (statusCounts?.[filterStatus] ?? 0);
                const label =
                  filterStatus === 'ALL' ? copy.participants.allStatuses : statusCopy[filterStatus];
                return (
                  <button
                    key={filterStatus}
                    type="button"
                    onClick={() => selectStatus(filterStatus)}
                    aria-pressed={isSelected}
                    className={`inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-xs font-label font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 ${
                      isSelected
                        ? 'bg-primary text-on-primary'
                        : 'bg-surface-container-lowest text-on-surface hover:bg-surface-container-high'
                    }`}
                  >
                    <Bilingual {...label} />
                    <span
                      className={`tabular-nums ${isSelected ? 'text-on-primary/75' : 'text-on-surface-variant'}`}
                    >
                      {count.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        aria-label={t(copy.participants.directoryLabel)}
      >
        <article className="rounded-2xl bg-surface-container-lowest p-5 shadow-level1">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            <Bilingual {...copy.participants.totalEnrollments} />
          </p>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums text-on-surface">
            {totalMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
          </p>
        </article>
        <article className="rounded-2xl bg-surface-container-lowest p-5 shadow-level1">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            <Bilingual {...copy.participants.averageProgress} />
          </p>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums text-primary">
            {data?.summary.averageProgress ?? 0}%
          </p>
        </article>
        <article className="rounded-2xl bg-success-container p-5 text-on-success-container">
          <div className="flex items-start justify-between gap-3">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
              <Bilingual {...copy.participants.activeEnrollments} />
            </p>
            <CheckCircle2 size={17} />
          </div>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums">
            {activeMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
          </p>
        </article>
        <article className="rounded-2xl bg-error-container p-5 text-on-error-container">
          <div className="flex items-start justify-between gap-3">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
              <Bilingual {...copy.participants.attentionOnPage} />
            </p>
            <AlertCircle size={17} />
          </div>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums">
            {attentionCount.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
          </p>
          <p className="mt-2 text-xs">
            <Bilingual {...copy.participants.attentionDescription} />
          </p>
        </article>
      </section>

      <section
        className="rounded-3xl bg-surface-container-low p-4 shadow-level1 sm:p-5"
        aria-labelledby="participant-directory-title"
      >
        <div className="flex flex-col gap-3 px-1 pb-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2
              id="participant-directory-title"
              className="m-0 font-headline text-xl font-extrabold text-on-surface"
            >
              <Bilingual {...copy.participants.directoryTitle} />
            </h2>
            {currentRange ? (
              <p className="mt-1 text-xs text-on-surface-variant">
                {t(copy.participants.resultRange)
                  .replace('{from}', String(currentRange.from))
                  .replace('{to}', String(currentRange.to))
                  .replace('{total}', String(currentRange.total))}
              </p>
            ) : null}
          </div>
          {isFetching && !isLoading ? (
            <span className="inline-flex items-center gap-2 text-xs font-label font-bold text-primary">
              <Loader2 size={14} className="animate-spin" />
              <Bilingual {...copy.participants.refreshing} />
            </span>
          ) : null}
        </div>
        {selectedEnrollmentIds.length > 0 && (
          <div className="mb-4 flex flex-col gap-3 rounded-2xl bg-error-container/70 p-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="m-0 text-sm font-label font-bold text-on-error-container">
              {t(copy.participants.selectedCount).replace(
                '{count}',
                String(selectedEnrollmentIds.length)
              )}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedEnrollmentIds([])}
                aria-label={t(copy.common.cancel)}
                className="inline-flex min-h-10 items-center justify-center rounded-xl bg-surface-container-lowest px-3 text-xs font-label font-bold text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-error active:scale-95"
              >
                <X size={15} aria-hidden="true" />
              </button>
              <Button
                onClick={() => openRemovalConfirmation(selectedEnrollmentIds)}
                className="min-h-10 bg-error px-4 text-xs font-label font-bold text-on-error hover:bg-error/90"
              >
                <Trash2 size={15} aria-hidden="true" />
                <Bilingual {...copy.participants.removeAccess} />
              </Button>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="grid gap-3" aria-busy="true">
            {[1, 2, 3, 4, 5].map(item => (
              <div
                key={item}
                className="h-24 animate-shimmer rounded-2xl bg-surface-container-lowest"
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-error-container px-6 py-12 text-center text-on-error-container">
            <AlertCircle size={28} className="mx-auto" />
            <p className="mt-3 text-sm font-body">
              <Bilingual {...copy.participants.failedLoad} />
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void refetch()}
              className="mt-4 bg-surface-container-lowest text-on-surface hover:bg-surface-container-high"
            >
              <Bilingual {...copy.participants.retry} />
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden lg:grid lg:grid-cols-[2.5rem_minmax(0,1.25fr)_minmax(0,1.15fr)_minmax(11rem,1fr)_minmax(8rem,0.7fr)_2.5rem_2.5rem] lg:gap-5 rounded-2xl bg-surface-container-high px-6 py-3">
              <label className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleVisibleEnrollmentSelection}
                  aria-label={t(copy.participants.selectAllOnPage)}
                  className="size-4 cursor-pointer accent-primary"
                />
              </label>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderParticipant} />
              </div>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderActiveProgram} />
              </div>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderProgressPath} />
              </div>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderCurrentStatus} />
              </div>
              <div />
              <div />
            </div>

            <div className="mt-3 space-y-3">
              {filteredEnrollments.map(enrollment => (
                <article
                  key={enrollment.id}
                  className={`grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-start gap-2 rounded-2xl p-3 transition-colors duration-150 lg:grid-cols-[2.5rem_minmax(0,1.25fr)_minmax(0,1.15fr)_minmax(11rem,1fr)_minmax(8rem,0.7fr)_2.5rem_2.5rem] lg:items-center lg:gap-5 lg:p-5 ${
                    selectedEnrollmentIdSet.has(enrollment.id)
                      ? 'bg-primary/10'
                      : 'bg-surface-container-lowest hover:bg-surface-container-high'
                  }`}
                >
                  <label className="flex min-h-11 items-center justify-center lg:col-start-1">
                    <input
                      type="checkbox"
                      checked={selectedEnrollmentIdSet.has(enrollment.id)}
                      onChange={() => toggleEnrollmentSelection(enrollment.id)}
                      aria-label={`${t(copy.participants.selectEnrollment)}: ${enrollment.user?.name ?? enrollment.user?.email ?? ''}`}
                      className="size-4 cursor-pointer accent-primary"
                    />
                  </label>
                  <Link
                    to={`/participants/${enrollment.id}`}
                    aria-label={`${t(copy.participants.openDossier)}: ${enrollment.user?.name ?? enrollment.user?.email ?? ''}`}
                    className="group col-start-2 flex min-w-0 flex-col gap-4 rounded-xl p-2 no-underline transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary lg:col-span-5 lg:col-start-2 lg:grid lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1.15fr)_minmax(11rem,1fr)_minmax(8rem,0.7fr)_2.5rem] lg:items-center lg:gap-5 lg:p-0"
                  >
                  {/* Participant Name */}
                  <div className="flex w-full items-start justify-between lg:block">
                    <div>
                      <div className="text-sm font-body font-bold text-on-surface group-hover:text-primary transition-colors">
                        {enrollment.user?.name}
                      </div>
                      <div className="text-[10px] font-mono text-on-surface-variant opacity-60">
                        {enrollment.user?.email}
                      </div>
                    </div>
                    <ChevronRight size={18} className="shrink-0 text-primary lg:hidden" />
                  </div>

                  {/* Active Program */}
                  <div className="flex w-full items-center justify-between lg:w-auto">
                    <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant lg:hidden">
                      <Bilingual {...copy.participants.tableHeaderActiveProgram} />
                    </span>
                    <div className="max-w-[150px] text-right text-sm font-body font-medium text-on-surface sm:max-w-[200px] lg:max-w-none lg:text-left">
                      {enrollment.program?.name}
                    </div>
                  </div>

                  {/* Progress Path */}
                  <div className="flex w-full items-center justify-between gap-4 lg:w-auto">
                    <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant lg:hidden">
                      <Bilingual {...copy.participants.tableHeaderProgressPath} />
                    </span>
                    <div className="flex flex-1 items-center justify-end gap-4 lg:justify-start lg:pr-4">
                      <div className="hidden w-24 sm:block lg:flex-1">
                        <ProgressBar value={enrollment.progress} size="sm" />
                      </div>
                      <div className="flex flex-col">
                        <span className="mb-1 text-sm font-bold text-on-surface">
                          {Math.round(enrollment.progress)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Current Status */}
                  <div className="flex w-full items-center justify-between lg:w-auto">
                    <span className="text-[10px] font-label font-bold uppercase tracking-widest text-on-surface-variant lg:hidden">
                      <Bilingual {...copy.participants.tableHeaderCurrentStatus} />
                    </span>
                    <StatusChip
                      label={getStatusLabel(enrollment.status)}
                      variant={enrollment.status === 'ACTIVE' ? 'success' : 'neutral'}
                    />
                  </div>

                  {/* Actions (Desktop) */}
                  <div className="hidden justify-end text-primary lg:flex">
                    <ChevronRight
                      size={18}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => openRemovalConfirmation([enrollment.id])}
                    className="col-start-3 inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-error transition-colors hover:bg-error-container focus-visible:outline focus-visible:outline-2 focus-visible:outline-error active:scale-95 lg:col-start-7"
                    aria-label={`${t(copy.participants.removeAccess)}: ${enrollment.user?.name ?? enrollment.user?.email ?? ''}`}
                    title={t(copy.participants.removeAccess)}
                  >
                    <Trash2 size={17} aria-hidden="true" />
                  </button>
                </article>
              ))}
            </div>

            {filteredEnrollments.length === 0 && (
              <div className="rounded-2xl bg-surface-container-lowest px-8 py-16 text-center text-sm text-on-surface-variant">
                <PauseCircle size={28} className="mx-auto text-primary/50" />
                <p className="mt-3">
                  <Bilingual {...copy.participants.emptyState} />
                </p>
                {(search || status !== 'ALL') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="mt-3 bg-surface-container-high text-on-surface hover:bg-surface-container-highest"
                  >
                    <Bilingual {...copy.participants.resetFilters} />
                  </Button>
                )}
              </div>
            )}

            {data?.meta && data.meta.totalPages > 1 ? (
              <nav
                className="mt-5 flex items-center justify-between gap-3 rounded-2xl bg-surface-container-lowest px-4 py-3"
                aria-label={t(copy.participants.directoryTitle)}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(data.meta.page - 1)}
                  disabled={data.meta.page === 1}
                  leftIcon={<ChevronLeft size={16} />}
                  className="whitespace-nowrap bg-surface-container-low text-on-surface hover:bg-surface-container-high disabled:opacity-40"
                >
                  <Bilingual {...copy.common.previous} />
                </Button>
                <span className="text-xs font-body tabular-nums text-on-surface-variant">
                  <strong className="text-on-surface">{data.meta.page}</strong> /{' '}
                  {data.meta.totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(data.meta.page + 1)}
                  disabled={data.meta.page === data.meta.totalPages}
                  rightIcon={<ChevronRight size={16} />}
                  className="whitespace-nowrap bg-surface-container-low text-on-surface hover:bg-surface-container-high disabled:opacity-40"
                >
                  <Bilingual {...copy.common.next} />
                </Button>
              </nav>
            ) : null}
          </>
        )}
      </section>
      {removalEnrollmentIds.length > 0 && (
        <div
          className="fixed inset-0 z-[70] flex items-end bg-on-surface/40 p-4 backdrop-blur-sm sm:items-center sm:justify-center"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) closeRemovalConfirmation();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-enrollment-access-title"
            className="w-full max-w-lg rounded-3xl bg-surface-container-lowest p-6 shadow-level5 sm:p-7"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-error">
                  <Bilingual {...copy.participants.removeAccess} />
                </p>
                <h2
                  id="remove-enrollment-access-title"
                  className="mt-2 font-headline text-2xl font-extrabold text-on-surface"
                >
                  <Bilingual {...copy.participants.removeAccessTitle} />
                </h2>
              </div>
              <button
                type="button"
                onClick={closeRemovalConfirmation}
                disabled={isRemovingAccess}
                aria-label={t(copy.common.cancel)}
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-surface-container-low text-on-surface-variant transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-error disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <p className="mt-4 text-sm leading-6 text-on-surface-variant">
              <Bilingual {...copy.participants.removeAccessDescription} />
            </p>
            <p className="mt-3 text-xs font-label font-bold text-error">
              {t(copy.participants.selectedCount).replace(
                '{count}',
                String(removalEnrollmentIds.length)
              )}
            </p>
            <div className="mt-6">
              <label
                htmlFor="remove-enrollment-access-reason"
                className="block text-sm font-bold text-on-surface"
              >
                <Bilingual {...copy.participants.removeAccessReason} />
              </label>
              <textarea
                id="remove-enrollment-access-reason"
                value={removalReason}
                onChange={event => setRemovalReason(event.target.value)}
                rows={3}
                minLength={5}
                maxLength={500}
                disabled={isRemovingAccess}
                placeholder={t(copy.participants.removeAccessReasonPlaceholder)}
                className="mt-2 min-h-28 w-full resize-y rounded-2xl bg-surface-container-low px-4 py-3 text-sm leading-6 text-on-surface outline-none placeholder:text-on-surface-variant/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl bg-surface-container-low p-4 text-sm leading-6 text-on-surface-variant">
              <input
                type="checkbox"
                checked={isRemovalConfirmed}
                onChange={event => setIsRemovalConfirmed(event.target.checked)}
                disabled={isRemovingAccess}
                className="mt-1 size-4 shrink-0 cursor-pointer accent-error disabled:cursor-not-allowed"
              />
              <span>
                <Bilingual {...copy.participants.removeAccessConfirm} />
              </span>
            </label>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="ghost"
                onClick={closeRemovalConfirmation}
                disabled={isRemovingAccess}
                className="min-h-11 bg-surface-container-low text-on-surface hover:bg-surface-container-high"
              >
                <Bilingual {...copy.common.cancel} />
              </Button>
              <Button
                onClick={submitRemoval}
                disabled={
                  isRemovingAccess || removalReason.trim().length < 5 || !isRemovalConfirmed
                }
                className="min-h-11 bg-error text-on-error hover:bg-error/90 disabled:opacity-50"
              >
                {isRemovingAccess ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                <Bilingual {...copy.participants.removeAccessConfirmAction} />
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
